import { SeverityBadge } from "./SeverityBadge";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { ArrowUpRight, Info, Sparkles, BookmarkPlus, EyeOff, Check, ChevronDown, ChevronUp, Wrench } from "lucide-react";
import { useState, useEffect } from "react";
import { createCheckout } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import IssueFeedbackInline from "@/components/feedback/IssueFeedbackInline";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

function formatRange(r) {
    if (!r) return "—";
    const fmt = (n) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
        }).format(n);
    return `${fmt(r.low)} – ${fmt(r.high)}`;
}

function FactorLabel({ factor }) {
    const map = {
        steep_pitch: "Steep pitch",
        multi_story: "Multi-story access",
        limited_access: "Limited access",
        tile_or_specialty: "Specialty material",
        permit_required: "Permits required",
        asbestos_concern: "Possible asbestos",
        lead_paint_concern: "Possible lead paint",
        structural_implications: "Structural implications",
        active_water: "Active moisture",
        remote_location: "Remote mobilization",
        historic_property: "Historic requirements",
        high_cost_finish: "High-end finish",
    };
    return <span className="bl-badge bl-badge-neutral">{map[factor] || factor}</span>;
}

function CostExplainer({ exposure }) {
    if (!exposure) return null;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="text-bl-muted hover:text-bl-primary inline-flex items-center gap-1 text-xs"
                    data-testid="cost-explainer-trigger"
                    title="Why this estimate"
                >
                    <Info className="w-3.5 h-3.5" /> Why
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="bg-bl-surface border-bl text-bl w-80 z-50"
                align="start"
                data-testid="cost-explainer-popover"
            >
                <div className="bl-eyebrow">Why this estimate</div>
                <p className="mt-2 text-sm leading-relaxed">
                    {exposure.scope_label && (
                        <>
                            Engine-matched scope:{" "}
                            <span className="font-mono-bl text-xs">
                                {exposure.scope_label}
                            </span>
                            .
                            <br />
                        </>
                    )}
                    {exposure.market_profile && (
                        <>
                            Localized for{" "}
                            <span className="text-bl">{exposure.market_profile}</span>.
                            <br />
                        </>
                    )}
                    {exposure.permit_likely && "Permits & inspections likely required. "}
                    {exposure.contractor_minimum_applied &&
                        "A contractor minimum was applied — small-job pricing reflects mobilization and overhead. "}
                </p>
                {exposure.complexity_factors_applied?.length > 0 && (
                    <div className="mt-3">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-2">
                            Complexity factors applied
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {exposure.complexity_factors_applied.map((f) => (
                                <FactorLabel key={f} factor={f} />
                            ))}
                        </div>
                    </div>
                )}
                {exposure.confidence_reasons?.length > 0 && (
                    <ul className="mt-3 space-y-1.5 text-xs text-bl-muted leading-relaxed">
                        {exposure.confidence_reasons.map((r, i) => (
                            <li key={i} className="flex gap-1.5">
                                <span className="text-bl-primary">·</span>
                                <span>{r}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="mt-3 text-[10px] text-bl-muted">
                    Source: Buyer's Leverage Repair Exposure Engine ·{" "}
                    {exposure.engine_version || "v1"}
                </p>
            </PopoverContent>
        </Popover>
    );
}

export default function IssueCard({ issue, locked = false, ownedTier = null, reportId = null, uploadId = null, userState = null, onStateChange = null, onGetQuote = null, defaultCollapsed = false }) {
    const exposure = issue.cost_exposure;
    const showUpgrade = ownedTier === "summary" && !locked && uploadId;
    const [upgrading, setUpgrading] = useState(false);
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    // Sync with parent's collapse-all toggle.
    useEffect(() => {
        setCollapsed(defaultCollapsed);
    }, [defaultCollapsed]);

    const reviewed = !!userState?.reviewed_at;
    const saved = !!userState?.saved_for_later;
    const hidden = !!userState?.hidden_by_user;
    const plainTerms = Array.isArray(issue.plain_english_terms) ? issue.plain_english_terms : [];
    const [plainOpen, setPlainOpen] = useState(false);

    const updateState = (patch) => {
        // Optimistic — parent handles persistence + refresh.
        onStateChange?.(issue.issue_id, patch);
    };

    const startUpgrade = async () => {
        try {
            setUpgrading(true);
            track("upsell_clicked", {
                product: "closing_upgrade",
                source: "issue-card",
                report_id: reportId,
                issue_id: issue?.issue_id,
            });
            const r = await createCheckout("upgrade", uploadId);
            window.location.href = r.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start upgrade.");
            setUpgrading(false);
        }
    };

    return (
        <article
            className={
                "bl-card bl-card-hover p-6 md:p-8 " +
                (reviewed ? "opacity-80 " : "") +
                (hidden ? "opacity-50 " : "")
            }
            data-testid={`issue-card-${issue.issue_id || "x"}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <SeverityBadge severity={issue.severity} />
                        <span className="text-[11px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                            {issue.category}
                        </span>
                        {reviewed && (
                            <span
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase text-bl-primary"
                                data-testid={`issue-reviewed-pill-${issue.issue_id}`}
                            >
                                <Check className="w-2.5 h-2.5" /> Reviewed
                            </span>
                        )}
                        {saved && !reviewed && (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase text-bl-primary">
                                <BookmarkPlus className="w-2.5 h-2.5" /> Saved
                            </span>
                        )}
                    </div>
                    <h3 className="text-lg md:text-xl font-display font-semibold text-bl leading-snug">
                        {issue.title}
                    </h3>
                </div>
                {/* Per-card collapse toggle */}
                <button
                    type="button"
                    onClick={() => setCollapsed(!collapsed)}
                    className="text-bl-muted hover:text-bl flex-shrink-0 p-1.5"
                    aria-label={collapsed ? "Expand" : "Collapse"}
                    data-testid={`issue-collapse-${issue.issue_id}`}
                >
                    {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
            </div>

            {!collapsed && (<>
            <p className="mt-4 text-bl leading-relaxed">{issue.simplified_explanation}</p>

            {plainTerms.length > 0 && (
                <div className="mt-3" data-testid={`issue-plain-english-${issue.issue_id}`}>
                    <button
                        type="button"
                        onClick={() => setPlainOpen(!plainOpen)}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-bl-primary hover:text-bl-primary/80"
                        data-testid={`issue-plain-english-toggle-${issue.issue_id}`}
                    >
                        <Info className="w-3 h-3" />
                        {plainOpen ? "Hide plain-English terms" : `Plain English (${plainTerms.length})`}
                    </button>
                    {plainOpen && (
                        <dl className="mt-3 space-y-2 rounded-xl bg-bl-surface border border-bl p-4">
                            {plainTerms.map((t, i) => (
                                <div key={i} className="text-sm">
                                    <dt className="font-display font-semibold text-bl">
                                        {t.term}
                                    </dt>
                                    <dd className="mt-0.5 text-bl-muted leading-relaxed">
                                        {t.explanation}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            )}

            {issue.why_it_matters && (
                <div className="mt-5 pl-4 border-l-2 border-bl">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                        Why it matters
                    </div>
                    <p className="text-[15px] text-bl leading-relaxed">
                        {issue.why_it_matters}
                    </p>
                </div>
            )}

            {/* Exposure block */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5 flex items-center gap-2">
                        Estimated Professional Repair Exposure
                        {!locked && <CostExplainer exposure={exposure} />}
                    </div>
                    <div className="font-mono-bl tnum text-lg text-bl" data-testid={`issue-exposure-${issue.issue_id}`}>
                        {locked ? (
                            <span className="bl-blur-locked">
                                {formatRange(issue.estimated_repair_range_usd)}
                            </span>
                        ) : (
                            formatRange(issue.estimated_repair_range_usd)
                        )}
                    </div>
                    {!locked && exposure && (
                        <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                            <ConfidenceBadge confidence={exposure.confidence} reasons={exposure.confidence_reasons} />
                            {exposure.contractor_minimum_applied && (
                                <span className="bl-badge bl-badge-neutral text-[10px]">
                                    Includes contractor minimum
                                </span>
                            )}
                            {exposure.permit_likely && (
                                <span className="bl-badge bl-badge-neutral text-[10px]">
                                    Permits likely
                                </span>
                            )}
                            {exposure.width_capped && (
                                <span
                                    className="bl-badge bl-badge-neutral text-[10px]"
                                    title="Range tightened by Buyer's Leverage guardrails to stay decision-useful"
                                >
                                    Tightened range
                                </span>
                            )}
                        </div>
                    )}
                    {!locked && (
                        <div className="mt-2 text-[11px] text-bl-muted" data-testid={`localized-${issue.issue_id}`}>
                            {exposure?.localized && exposure?.zip_code_used
                                ? <>Localized using ZIP <span className="font-mono-bl text-bl">{exposure.zip_code_used}</span></>
                                : <>ZIP not provided — estimate uses general contractor-level pricing.</>}
                        </div>
                    )}
                </div>
                <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                        Timeline
                    </div>
                    <div className="text-[15px] text-bl">
                        {issue.timeline === "immediate"
                            ? "Immediate Attention"
                            : issue.timeline === "3_to_12_months"
                              ? "3–12 Month Planning"
                              : "Long-Term Monitoring"}
                    </div>
                    {!locked && exposure?.market_profile && (
                        <div className="mt-2 text-xs text-bl-muted">
                            Localized for{" "}
                            <span className="text-bl">{exposure.market_profile}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* AI qualitative reasoning */}
            {!locked && exposure && (exposure.cost_drivers?.length || exposure.confidence_reason || exposure.assumptions?.length) && (
                <div className="mt-5 bl-card p-4 bg-bl-bg/30" data-testid={`exposure-reasoning-${issue.issue_id}`}>
                    {exposure.confidence_reason && (
                        <div className="mb-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1">
                                Why this range may vary
                            </div>
                            <p className="text-sm text-bl leading-relaxed">{exposure.confidence_reason}</p>
                        </div>
                    )}
                    {!!exposure.cost_drivers?.length && (
                        <div className="mb-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                                Cost drivers
                            </div>
                            <ul className="flex flex-wrap gap-1.5">
                                {exposure.cost_drivers.map((d, i) => (
                                    <li key={i} className="bl-badge bl-badge-neutral text-[11px]">{d}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {!!exposure.assumptions?.length && (
                        <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1">
                                Assumptions
                            </div>
                            <ul className="list-disc list-inside text-sm text-bl-muted leading-relaxed">
                                {exposure.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {issue.recommended_next_step && (
                <div className="mt-5">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1.5">
                        Recommended next step
                    </div>
                    <p className="text-[15px] text-bl">{issue.recommended_next_step}</p>
                </div>
            )}

            <div className="mt-6 pt-5 border-t border-bl flex flex-wrap items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 text-bl-muted flex-wrap">
                    {issue.specialist && (
                        <span className="bl-badge bl-badge-neutral">
                            {issue.specialist}
                        </span>
                    )}
                </div>
                {issue.source_reference && (
                    <span className="font-mono-bl text-xs text-bl-muted">
                        {issue.source_reference}
                    </span>
                )}
            </div>

            {issue.negotiation_relevance && !locked && (
                <div className="mt-6 bg-bl-teal-tint rounded-xl p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-1.5">
                        <ArrowUpRight className="w-3.5 h-3.5 text-bl-primary" />
                        <span className="text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium">
                            Negotiation relevance
                        </span>
                    </div>
                    <p className="text-[15px] text-bl leading-relaxed">
                        {issue.negotiation_relevance}
                    </p>
                </div>
            )}
            </>)}

            {/* Buyer action row — frontend/DB only, never AI. Always visible
                even when card is collapsed, so the user can act on a finding
                without re-expanding it. */}
            {!locked && onStateChange && (
                <div
                    className="mt-5 flex flex-wrap items-center gap-2 text-xs"
                    data-testid={`issue-actions-${issue.issue_id}`}
                >
                    <button
                        type="button"
                        onClick={() => updateState({ reviewed: !reviewed })}
                        className={
                            "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition " +
                            (reviewed
                                ? "bg-bl-teal-tint border-bl-primary text-bl-primary"
                                : "bg-bl-surface border-bl text-bl-muted hover:text-bl")
                        }
                        data-testid={`issue-action-review-${issue.issue_id}`}
                    >
                        <Check className="w-3 h-3" />
                        {reviewed ? "Reviewed" : "Mark reviewed"}
                    </button>
                    <button
                        type="button"
                        onClick={() => updateState({ saved_for_later: !saved })}
                        className={
                            "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition " +
                            (saved
                                ? "bg-bl-teal-tint border-bl-primary text-bl-primary"
                                : "bg-bl-surface border-bl text-bl-muted hover:text-bl")
                        }
                        data-testid={`issue-action-save-${issue.issue_id}`}
                    >
                        <BookmarkPlus className="w-3 h-3" />
                        {saved ? "Saved" : "Save for later"}
                    </button>
                    {onGetQuote && (
                        <button
                            type="button"
                            onClick={() => {
                                track("quote_cta_clicked", { source: "issue-card", issue_id: issue.issue_id });
                                onGetQuote(issue);
                            }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-bl-surface border border-bl text-bl-muted hover:text-bl-primary hover:border-bl-primary transition"
                            data-testid={`issue-action-quote-${issue.issue_id}`}
                            title="Request local quotes for this scope (optional, on your terms)"
                        >
                            <Wrench className="w-3 h-3" />
                            Get a quote
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => updateState({ hidden_by_user: !hidden })}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-bl-surface border border-bl text-bl-muted hover:text-bl transition"
                        data-testid={`issue-action-hide-${issue.issue_id}`}
                        title={hidden ? "Unhide this issue" : "Hide this low-priority item from the default view"}
                    >
                        <EyeOff className="w-3 h-3" />
                        {hidden ? "Unhide" : "Hide"}
                    </button>
                    {reportId && (
                        <IssueFeedbackInline reportId={reportId} issue={issue} />
                    )}
                </div>
            )}

            {/* Per-issue upgrade nudge — only on Summary-tier reports */}
            {showUpgrade && (
                <button
                    onClick={startUpgrade}
                    disabled={upgrading}
                    className="mt-4 w-full text-left bl-card p-3.5 border-bl-primary/25 hover:border-bl-primary/60 transition flex items-center justify-between gap-3 group"
                    data-testid={`issue-upgrade-${issue.issue_id}`}
                >
                    <div className="flex items-start gap-2.5 flex-1">
                        <Sparkles className="w-4 h-4 text-bl-primary mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="text-bl text-sm font-medium">
                                Unlock the leverage angle for this issue
                            </div>
                            <div className="mt-0.5 text-bl-muted text-xs leading-relaxed">
                                Closing Strategy adds seller-pushback risk, suggested ask, and
                                sequencing for this finding.
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-bl-primary text-xs font-mono-bl whitespace-nowrap group-hover:translate-x-0.5 transition">
                        {upgrading ? "Starting…" : (
                            <>
                                +$50 <ArrowUpRight className="w-3.5 h-3.5" />
                            </>
                        )}
                    </div>
                </button>
            )}
        </article>
    );
}
